import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type CalEvent, type CalType, TYPE_META, ymdKey } from '../lib/calendarData';
import { gcalConnected, gcalConnect, gcalDisconnect, gcalList, gcalPushAll, type GEvent } from '../lib/gcal';

const WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const GOOGLE_COLOR = '#2dd4bf';

type FilterKey = CalType | 'google';
const FILTERS: { key: FilterKey; label: string; color: string }[] = [
  { key: 'task', label: 'Tasks', color: TYPE_META.task.color },
  { key: 'followup', label: 'Follow-ups', color: TYPE_META.followup.color },
  { key: 'meeting', label: 'Meetings', color: TYPE_META.meeting.color },
  { key: 'reorder', label: 'Reorders', color: TYPE_META.reorder.color },
  { key: 'google', label: 'Google', color: GOOGLE_COLOR },
];

const colorFor = (e: CalEvent) => (e.source === 'google' ? GOOGLE_COLOR : TYPE_META[e.type].color);
const monthName = (d: Date) => d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
const sameYMD = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function mapGoogle(items: GEvent[]): CalEvent[] {
  return items
    .filter((g) => !(g.extendedProperties?.private?.mfxSource === 'mfx-crm')) // hide our own pushed events (already shown as CRM)
    .map((g) => {
      const startStr = g.start?.dateTime || g.start?.date || '';
      const allDay = !g.start?.dateTime;
      const date = startStr ? new Date(startStr) : new Date();
      const time = allDay ? undefined : date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      return { id: `g_${g.id}`, date, allDay, time, title: g.summary || '(no title)', type: 'meeting' as CalType, detail: g.description, source: 'google' as const, link: g.htmlLink };
    });
}

export default function MonthCalendar({ crmEvents }: { crmEvents: CalEvent[] }) {
  const nav = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [active, setActive] = useState<Set<FilterKey>>(new Set(['task', 'followup', 'meeting', 'reorder', 'google']));
  const [selected, setSelected] = useState<Date | null>(today);
  const [connected, setConnected] = useState(gcalConnected());
  const [gEvents, setGEvents] = useState<CalEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const gridStart = useMemo(() => { const d = new Date(monthStart); d.setDate(1 - d.getDay()); d.setHours(0, 0, 0, 0); return d; }, [monthStart]);
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; }), [gridStart]);

  const fetchGoogle = async () => {
    if (!gcalConnected()) { setConnected(false); return; }
    setBusy(true); setStatus(null);
    try {
      const min = new Date(gridStart); const max = new Date(gridStart); max.setDate(max.getDate() + 42);
      const items = await gcalList(min, max);
      setGEvents(mapGoogle(items));
      setConnected(true);
    } catch (e: any) {
      setStatus(e?.message || 'Google fetch failed.');
      setConnected(gcalConnected());
    } finally { setBusy(false); }
  };

  useEffect(() => { fetchGoogle(); /* eslint-disable-next-line */ }, [gridStart.getTime(), connected]);

  const connect = async () => {
    setBusy(true); setStatus(null);
    try {
      const ok = await gcalConnect();
      setConnected(ok);
      if (ok) { setStatus('Connected to Google Calendar.'); await fetchGoogle(); }
      else setStatus('Could not get Google Calendar access.');
    } catch (e: any) { setStatus(e?.message || 'Google connect failed.'); }
    finally { setBusy(false); }
  };

  const disconnect = () => { gcalDisconnect(); setConnected(false); setGEvents([]); setStatus('Disconnected.'); };

  const pushAll = async () => {
    setBusy(true); setStatus(null);
    try {
      const payload = crmEvents.map((e) => ({ key: e.id, title: e.title, date: e.date, allDay: e.allDay, time: e.time, detail: [e.client, e.detail].filter(Boolean).join(' — ') }));
      const r = await gcalPushAll(payload);
      setStatus(`Pushed ${r.pushed} event${r.pushed === 1 ? '' : 's'} to Google${r.failed ? ` · ${r.failed} failed` : ''}.`);
      await fetchGoogle();
    } catch (e: any) { setStatus(e?.message || 'Push failed.'); }
    finally { setBusy(false); }
  };

  const all = useMemo(() => [...crmEvents, ...gEvents].filter((e) => active.has(e.source === 'google' ? 'google' : e.type)), [crmEvents, gEvents, active]);
  const byDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of all) { const k = ymdKey(e.date); (m.get(k) || m.set(k, []).get(k)!).push(e); }
    for (const v of m.values()) v.sort((a, b) => (a.allDay === b.allDay ? a.date.getTime() - b.date.getTime() : a.allDay ? -1 : 1));
    return m;
  }, [all]);

  const toggle = (k: FilterKey) => setActive((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const selectedEvents = selected ? (byDay.get(ymdKey(selected)) || []) : [];

  const openEvent = (e: CalEvent) => {
    if (e.clientId) nav(`/client?id=${encodeURIComponent(e.clientId)}`);
    else if (e.link) window.open(e.link, '_blank', 'noreferrer');
  };

  return (
    <div className="mcal">
      <div className="mcal-bar">
        <div className="mcal-nav">
          <button className="mcal-arrow" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</button>
          <button className="mcal-today" onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setSelected(new Date()); }}>Today</button>
          <button className="mcal-arrow" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</button>
          <h3 className="mcal-month">{monthName(cursor)}</h3>
        </div>
        <div className="mcal-google">
          {connected ? (
            <>
              <span className="gc-pill on"><i /> Google connected</span>
              <button className="btn small" onClick={pushAll} disabled={busy}>{busy ? 'Working…' : '↑ Push CRM → Google'}</button>
              <button className="btn small ghost" onClick={fetchGoogle} disabled={busy}>↻ Refresh</button>
              <button className="btn small ghost" onClick={disconnect} disabled={busy}>Disconnect</button>
            </>
          ) : (
            <button className="btn small primary" onClick={connect} disabled={busy}>{busy ? 'Connecting…' : 'Connect Google Calendar'}</button>
          )}
        </div>
      </div>

      <div className="mcal-legend">
        {FILTERS.map((f) => (
          <button key={f.key} className={`mcal-chip${active.has(f.key) ? ' on' : ''}`} onClick={() => toggle(f.key)}>
            <span className="dot" style={{ background: f.color }} />{f.label}
          </button>
        ))}
      </div>
      {status && <div className="mcal-status">{status}</div>}

      <div className="mcal-grid">
        {WEEK.map((w) => <div key={w} className="mcal-wd">{w}</div>)}
        {days.map((d) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const evs = byDay.get(ymdKey(d)) || [];
          const isToday = sameYMD(d, today);
          const isSel = selected && sameYMD(d, selected);
          return (
            <button key={d.toISOString()} className={`mcal-day${inMonth ? '' : ' out'}${isToday ? ' today' : ''}${isSel ? ' sel' : ''}`} onClick={() => setSelected(d)}>
              <span className="mcal-dnum">{d.getDate()}</span>
              <span className="mcal-chips">
                {evs.slice(0, 3).map((e) => (
                  <span key={e.id} className="mcal-ev" title={e.title} onClick={(ev) => { ev.stopPropagation(); openEvent(e); }}>
                    <i style={{ background: colorFor(e) }} />
                    <span className="mcal-ev-t">{e.time ? `${e.time} ` : ''}{e.title}</span>
                  </span>
                ))}
                {evs.length > 3 && <span className="mcal-more">+{evs.length - 3} more</span>}
              </span>
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="mcal-detail">
          <div className="mcal-detail-h">{selected.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}<span>{selectedEvents.length} event{selectedEvents.length === 1 ? '' : 's'}</span></div>
          {selectedEvents.length === 0 && <div className="empty">Nothing scheduled.</div>}
          <div className="mcal-detail-list">
            {selectedEvents.map((e) => (
              <button key={e.id} className="mcal-row" onClick={() => openEvent(e)}>
                <i className="mcal-row-dot" style={{ background: colorFor(e) }} />
                <div className="mcal-row-main">
                  <strong>{e.title}</strong>
                  <span className="mcal-row-sub">{[e.allDay ? 'All day' : e.time, e.client, e.detail].filter(Boolean).join(' · ')}</span>
                </div>
                <span className="mcal-row-kind">{e.source === 'google' ? 'Google' : TYPE_META[e.type].label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
